import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import { UserFeedbackItem } from '../../services/api';
import { SEARCE_COLORS } from '../../theme';
import RichTextView from '../common/RichTextView';

/** Format date as Searce standard: dd-mmm-yyyy (e.g. 10-Mar-2025) */
function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${h}:${min}`;
  } catch {
    return iso;
  }
}

export interface ViewFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  feedback: UserFeedbackItem | null;
}

const THUMB_SIZE = 100;
const CARD_ACTION_HEIGHT = 32;

const ViewFeedbackModal: React.FC<ViewFeedbackModalProps> = ({ open, onClose, feedback }) => {
  const [failedImageIndices, setFailedImageIndices] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (feedback) setFailedImageIndices(new Set());
  }, [feedback?.id]);

  const handleImageError = (index: number) => {
    setFailedImageIndices((prev) => new Set(prev).add(index));
  };

  if (!feedback) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '1.1rem',
          py: 2.5,
          px: 3,
          borderBottom: `1px solid ${SEARCE_COLORS.border ?? '#e0e0e0'}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {feedback.subject}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {feedback.user_name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ mt: -0.5, mr: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 0, py: 0 }}>
        <Box sx={{ px: 3, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              mb: 2.5,
            }}
          >
            <Chip label={feedback.category} size="small" sx={{ fontWeight: 500 }} />
            {feedback.resolved_at && (
              <Chip
                label={`Resolved ${formatDateTime(feedback.resolved_at)}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {feedback.user_email}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
              · 
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(feedback.submitted_at)}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              letterSpacing: 1,
              display: 'block',
              mb: 1.5,
            }}
          >
            Feedback
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              maxHeight: 400,
              overflowY: 'auto',
            }}
          >
            <RichTextView html={feedback.feedback || '<p></p>'} />
          </Box>

          {feedback.reference_urls && feedback.reference_urls.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  letterSpacing: 1,
                  display: 'block',
                  mb: 1.5,
                }}
              >
                References
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mt: 2,
                }}
              >
                {feedback.reference_urls.map((url, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: THUMB_SIZE,
                      minWidth: THUMB_SIZE,
                      maxWidth: THUMB_SIZE,
                      flexShrink: 0,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      bgcolor: 'grey.100',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: SEARCE_COLORS.primary,
                      },
                    }}
                  >
                    <Box
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        width: '100%',
                        height: THUMB_SIZE,
                        minHeight: THUMB_SIZE,
                        maxHeight: THUMB_SIZE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        color: 'text.secondary',
                      }}
                    >
                      {failedImageIndices.has(index) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, p: 0.5 }}>
                          <InsertDriveFileIcon sx={{ fontSize: 32, color: SEARCE_COLORS.primary }} />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', textAlign: 'center', color: SEARCE_COLORS.primary }}>
                            Open link
                          </Typography>
                        </Box>
                      ) : (
                        <img
                          src={url}
                          alt={`Reference ${index + 1}`}
                          onError={() => handleImageError(index)}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: CARD_ACTION_HEIGHT,
                        minHeight: CARD_ACTION_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Link
                        href={url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.25,
                          fontSize: '0.65rem',
                          color: SEARCE_COLORS.primary,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: '0.75rem' }} />
                        Download
                      </Link>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewFeedbackModal;
